import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { useInvestigadorStats } from "../hooks/useInvestigadorStats";

const ExecutiveSummary = ({ setView }) => {
    // Hooks para obtener estadísticas del investigador
    const { stats, loading } = useInvestigadorStats();
    const activeProjectCode = localStorage.getItem("activeProjectCode") || "ACTIVO";

    return (
        <div className="container-fluid px-0">
            <div className="mb-3">
                <h2 className="fw-bold text-dark m-0 h4">Situación Epidemiológica Actual</h2>
                <p className="text-muted small mb-0">Análisis estadístico de la vigilancia epidemiológica</p>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-success" style={{ width: '3rem', height: '3rem' }}></div>
                    <p className="mt-3 text-muted fw-bold">Compilando datos del proyecto...</p>
                </div>
            ) : (
                <>
                    {/* Tarjetas de Indicadores Clave (KPIs) */}
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                                <div className="card-body p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div>
                                            <p className="text-muted smaller text-uppercase fw-bold mb-0" style={{ letterSpacing: '0.5px', fontSize: '0.7rem' }}>Muestras Analizadas</p>
                                            <h3 className="fw-bold text-dark m-0 h2" style={{ color: '#1a237e' }}>{stats.total}</h3>
                                        </div>
                                        <div className="bg-success bg-opacity-10 p-2 rounded-circle">
                                            <i className="bi bi-microscope text-success fs-4"></i>
                                        </div>
                                    </div>
                                    <div className="small text-muted border-top pt-2">
                                        <i className="bi bi-info-circle me-1"></i> Total histórico para el proyecto actual
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                                <div className="card-body p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div>
                                            <p className="text-muted smaller text-uppercase fw-bold mb-0" style={{ letterSpacing: '0.5px', fontSize: '0.7rem' }}>Actividad Reciente</p>
                                            <h3 className="fw-bold text-dark m-0 h2" style={{ color: '#1a237e' }}>{stats.active}</h3>
                                        </div>
                                        <div className="bg-warning bg-opacity-10 p-2 rounded-circle">
                                            <i className="bi bi-cursor-fill text-warning fs-4"></i>
                                        </div>
                                    </div>
                                    <div className="small text-muted border-top pt-2">
                                        <i className="bi bi-clock-history me-1"></i> Registros de campo en los últimos 30 días
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                                <div className="card-body p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div>
                                            <p className="text-muted smaller text-uppercase fw-bold mb-0" style={{ letterSpacing: '0.5px', fontSize: '0.7rem' }}>Incidencia Crítica</p>
                                            <h3 className="fw-bold text-danger m-0 h2">{stats.highRisk}%</h3>
                                        </div>
                                        <div className="bg-danger bg-opacity-10 p-2 rounded-circle">
                                            <i className="bi bi-exclamation-triangle-fill text-danger fs-4"></i>
                                        </div>
                                    </div>
                                    <div className="small text-muted border-top pt-2">
                                        <i className="bi bi-reception-4 me-1"></i> Porcentaje de muestras con riesgo <strong>ALTO</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row g-3">
                        {/* Gráfico de Distribución de Riesgo */}
                        <div className="col-12 col-lg-8">
                            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '20px' }}>
                                <div className="card-header bg-white border-0 pt-3 px-3 pb-0">
                                    <h6 className="fw-bold text-dark m-0">Nivel de Riesgo Epidemiológico</h6>
                                    <p className="text-muted smaller mb-0">Distribución proyectada</p>
                                </div>
                                <div className="card-body p-3" style={{ height: 280 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={stats.riskData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e0e0e0" />
                                            <XAxis type="number" fontSize={12} stroke="#9e9e9e" />
                                            <YAxis dataKey="name" type="category" width={80} fontSize={13} fontWeight={600} stroke="#455a64" />
                                            <RechartsTooltip
                                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                            />
                                            <Legend verticalAlign="top" height={36} />
                                            <Bar dataKey="cantidad" fill="#2e7d32" name="Casos Detectados" radius={[0, 8, 8, 0]} barSize={40}>
                                                {stats.riskData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        {/* Botones de Acción de Campo */}
                        <div className="col-12 col-lg-4">
                            <div className="card border-0 shadow-sm h-100 text-center p-3" style={{ borderRadius: '20px', background: 'linear-gradient(135deg, #ffffff 0%, #f9fbf9 100%)' }}>
                                <div className="mb-3">
                                    <i className="bi bi-lightning-charge-fill text-success fs-3"></i>
                                    <h6 className="mt-1 fw-bold text-dark">Acciones de Campo</h6>
                                    <p className="text-muted smaller mb-0">Registro inmediato</p>
                                </div>

                                <button className="btn btn-success w-100 mb-2 fw-bold py-2 shadow-sm" style={{ borderRadius: '10px' }} onClick={() => setView("registrarPaciente")}>
                                    <i className="bi bi-person-plus-fill me-2"></i> REGISTRAR PACIENTE
                                </button>

                                <button className="btn btn-outline-primary w-100 fw-bold py-2 mb-3" style={{ borderRadius: '10px', borderWidth: '2px' }} onClick={() => setView("buscarPaciente")}>
                                    <i className="bi bi-clipboard-pulse me-2"></i> REGISTRAR ANÁLISIS
                                </button>

                                <div className="py-2 px-2 rounded-3 bg-light text-start" style={{ fontSize: '0.7rem' }}>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <i className="bi bi-info-circle-fill text-primary"></i>
                                        <span className="fw-bold text-dark">Protocolo</span>
                                    </div>
                                    <p className="text-muted mb-0">
                                        Use bioseguridad antes de iniciar el procedimiento.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ExecutiveSummary;

